import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from '../services/authService';
import SavedItemsService from '../services/savedItemsService';
import SavedItemsServiceDB from '../services/savedItemsServiceDB';
import CollectionsService from '../services/collectionsService';
import TravelBoardsServiceDB from '../services/travelBoardsServiceDB';
import AlbumService from '../services/albumService';
import WalkingToursService from '../services/walkingToursService';
import TourismExperiencesService from '../services/tourismExperiencesService';
import { invalidateDashboardCache } from './useHomeDashboard';

const ACTIVITIES_PAGE_SIZE = 20;

// Module-level cache so the MySavedItems page renders instantly when the user
// returns within the TTL (e.g. SavedItems → Trip → SavedItems). Caches only
// the consolidated summary response, keyed by userId.
const savedItemsCache = new Map();
const SAVED_ITEMS_TTL_MS = 60_000;

const getCachedSummary = (userId) => {
  if (!userId) return null;
  const entry = savedItemsCache.get(userId);
  if (!entry) return null;
  if (Date.now() - entry.ts > SAVED_ITEMS_TTL_MS) {
    savedItemsCache.delete(userId);
    return null;
  }
  return entry.summary;
};

const setCachedSummary = (userId, summary) => {
  if (!userId) return;
  savedItemsCache.set(userId, { summary, ts: Date.now() });
};

// Exported so other pages can force a refresh on the user's next visit.
export const invalidateSavedItemsCache = (userId) => {
  if (!userId) {
    savedItemsCache.clear();
    return;
  }
  savedItemsCache.delete(userId);
};

// Shared taxonomy filter — used here to derive availableFilters and by
// MySavedItems for the Activities tab (which layers date filtering on top).
export function filterActivitiesByTaxonomy(activities, { segment, genre, subGenre }) {
  let filtered = activities;
  if (segment !== 'all') {
    filtered = filtered.filter(a => a.segment === segment);
  }
  if (genre !== 'all') {
    filtered = filtered.filter(a => a.genre === genre);
  }
  if (subGenre !== 'all') {
    filtered = filtered.filter(a => a.subGenre === subGenre);
  }
  return filtered;
}

// The category select's buckets. getActivityCategory names the outdoor one
// "parks"; the select (and the transform's fallback category) say "outdoor".
const CATEGORY_BUCKETS = ['food', 'culture', 'outdoor', 'entertainment', 'shopping', 'education'];

function activityCategoryBucket(activity) {
  if (CATEGORY_BUCKETS.includes(activity.category)) return activity.category;
  const bucket = SavedItemsServiceDB.getActivityCategory(
    [activity.category, ...(activity.tags || [])].filter(Boolean)
  );
  return bucket === 'parks' ? 'outdoor' : bucket;
}

// When a saved event stops being upcoming, in the viewer's local time: its
// start (eventDate + eventTime). With no time — or midnight, which the feeds
// use for "time not announced" — the end of its local day. Never
// new Date('YYYY-MM-DD'): that is UTC midnight, so a show tonight read as past
// from the morning, or from the evening before west of UTC (BH-21-04).
// Null when the date can't be read.
export function savedEventUpcomingUntil(eventDate, eventTime) {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(eventDate || ''));
  if (!day) return null;
  const [year, month, date] = [Number(day[1]), Number(day[2]) - 1, Number(day[3])];
  const time = /^(\d{1,2}):(\d{2})/.exec(String(eventTime || ''));
  const hours = time ? Number(time[1]) : 0;
  const minutes = time ? Number(time[2]) : 0;
  if (time && (hours > 0 || minutes > 0)) {
    return new Date(year, month, date, hours, minutes);
  }
  return new Date(year, month, date + 1);
}

// The Activities tab's filters (MySavedItems' applyFiltersToActivities): every
// control in the panel above it. Search reads the name and where it is, as
// All's search reads a place's title; category the place's bucket; tags keep
// the saves carrying every selected tag (BH-21-03).
export function filterSavedActivities(activities, filters, now = new Date()) {
  let filtered = filterActivitiesByTaxonomy(activities, {
    segment: filters.segment,
    genre: filters.genre,
    subGenre: filters.subGenre
  });

  const searchLower = (filters.search || '').trim().toLowerCase();
  if (searchLower) {
    filtered = filtered.filter(a =>
      [a.title, a.name, a.description, a.location, a.venue, a.city]
        .some(field => typeof field === 'string' && field.toLowerCase().includes(searchLower))
    );
  }

  if (filters.category && filters.category !== 'all') {
    filtered = filtered.filter(a => activityCategoryBucket(a) === filters.category);
  }

  if (filters.tags && filters.tags.length > 0) {
    filtered = filtered.filter(a => {
      const tags = a.tags || [];
      return filters.tags.every(tag => tags.includes(tag));
    });
  }

  if (filters.fromTrip) {
    filtered = filtered.filter(a =>
      a.fromTrip && String(a.fromTrip.tripId) === String(filters.fromTrip.tripId)
    );
  }

  // No date is not an event: always upcoming, never past. An unreadable date
  // stays upcoming too, rather than vanishing from both.
  if (filters.dateFilter === 'upcoming') {
    filtered = filtered.filter(a => {
      const until = a.eventDate ? savedEventUpcomingUntil(a.eventDate, a.eventTime) : null;
      return !until || now < until;
    });
  } else if (filters.dateFilter === 'past') {
    filtered = filtered.filter(a => {
      const until = a.eventDate ? savedEventUpcomingUntil(a.eventDate, a.eventTime) : null;
      return !!until && now >= until;
    });
  }

  return filtered;
}

/**
 * Custom hook for managing saved items data and state
 * Centralizes all data fetching and state management for MySavedItems
 *
 * Features:
 * - Consolidated API calls via summary endpoint (with fallback)
 * - Archive/unarchive for all item types
 * - Unified feed with filtering
 * - Error state handling
 * - Optimized lookups via memoized maps
 */
export function useSavedItemsData() {
  const { t } = useTranslation('savedItems');

  // Core data state
  const [currentUserId, setCurrentUserId] = useState(null);
  const [savedTrips, setSavedTrips] = useState([]);
  const [savedActivities, setSavedActivities] = useState([]);
  const [collections, setCollections] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [travelBoards, setTravelBoards] = useState([]);
  const [savedWalkingTours, setSavedWalkingTours] = useState([]);
  const [savedTourismExperiences, setSavedTourismExperiences] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  // Distinct {segment, genre, subGenre} combos across ALL the user's saves
  // (server-provided) — complete filter options regardless of pagination
  const [activityTaxonomy, setActivityTaxonomy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Archived state. Collections and boards are the only archivable types —
  // activities use "Remove from saved", albums live on their trip.
  const [archivedCollections, setArchivedCollections] = useState([]);
  const [archivedBoards, setArchivedBoards] = useState([]);
  const [archivedTotalCount, setArchivedTotalCount] = useState(0);

  // Pagination state for activities
  const [activitiesPagination, setActivitiesPagination] = useState({
    total: 0,
    offset: 0,
    hasMore: false,
    loading: false
  });

  // Board item IDs (for filtering uncategorized items)
  const [boardItemIds, setBoardItemIds] = useState({
    activity: [],
    trip: [],
    album: [],
    collection: []
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    tags: [],
    category: 'all',
    collection: 'all',
    segment: 'all',
    genre: 'all',
    subGenre: 'all',
    dateFilter: 'upcoming',
    // Trip drill-in: { tripId, tripName } — show only activities saved from
    // that trip (provenance via saved_items.source_trip_id)
    fromTrip: null
  });

  // Available filter options — from the server-provided taxonomy combos when
  // present (covers ALL saves, not just the loaded page), falling back to
  // loaded activities. Faceted: each dimension's options are narrowed by the
  // OTHER two selections only, so the user can switch (e.g.) segment directly
  // without clearing it first.
  const availableFilters = useMemo(() => {
    const source = activityTaxonomy.length > 0 ? activityTaxonomy : savedActivities;
    const selected = { segment: filters.segment, genre: filters.genre, subGenre: filters.subGenre };
    const optionsFor = (dimension, overrides) => [...new Set(
      filterActivitiesByTaxonomy(source, { ...selected, ...overrides })
        .map(a => a[dimension])
        .filter(Boolean)
    )];
    return {
      segments: optionsFor('segment', { segment: 'all' }),
      genres: optionsFor('genre', { genre: 'all' }),
      subGenres: optionsFor('subGenre', { subGenre: 'all' })
    };
  }, [activityTaxonomy, savedActivities, filters.segment, filters.genre, filters.subGenre]);

  // Load all saved items using consolidated summary endpoint (replaces 9+ API calls with 1)
  // Falls back to individual API calls if summary endpoint fails
  const loadSavedItems = useCallback(async ({ skipCache = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (currentUser?.id) {
        setCurrentUserId(currentUser.id);
      }

      // Try consolidated API call first
      let summary;
      const cached = !skipCache ? getCachedSummary(currentUser?.id) : null;
      if (cached) {
        summary = cached;
      } else {
        try {
          summary = await SavedItemsService.getSummary(ACTIVITIES_PAGE_SIZE, 0);
        } catch (summaryError) {
          console.warn('Summary endpoint failed, using fallback:', summaryError);
          summary = { success: false };
        }
        if (summary?.success && currentUser?.id) {
          setCachedSummary(currentUser.id, summary);
        }
      }

      if (summary.success) {
        // Set all state from consolidated response
        setSavedTrips(summary.trips || []);
        setSavedActivities(summary.activities?.items || []);
        setAvailableTags(summary.tags || []);
        setActivityTaxonomy(summary.activityTaxonomy || []);
        setCollections(summary.collections || []);
        setAlbums(summary.albums || []);
        setTravelBoards(summary.boards || []);
        setSavedWalkingTours(summary.walkingTours || []);
        setSavedTourismExperiences(summary.tourismExperiences || []);
        setBoardItemIds(summary.boardItemIds || { activity: [], trip: [], album: [], collection: [] });
        setArchivedTotalCount(summary.archivedCount || 0);

        if (summary.activities?.pagination) {
          setActivitiesPagination({
            total: summary.activities.pagination.total,
            offset: summary.activities.pagination.offset + (summary.activities.items?.length || 0),
            hasMore: summary.activities.pagination.hasMore,
            loading: false
          });
        } else {
          setActivitiesPagination({
            total: (summary.activities?.items || []).length,
            offset: (summary.activities?.items || []).length,
            hasMore: false,
            loading: false
          });
        }
      } else {
        // Fallback to individual API calls
        console.log('Using fallback: individual API calls');
        const activitiesResult = await SavedItemsService.getSavedActivities(null, {
          limit: ACTIVITIES_PAGE_SIZE,
          offset: 0
        });

        const [trips, tags, allCollections, boardsResult, activityIdsResult, tripIdsResult, albumIdsResult, collectionIdsResult, userAlbums] = await Promise.all([
          SavedItemsService.getSavedTrips(null, {}),
          SavedItemsService.getAllTags(),
          CollectionsService.getAllUserCollections(),
          TravelBoardsServiceDB.getUserBoards(),
          TravelBoardsServiceDB.getAllBoardItemIds('activity'),
          TravelBoardsServiceDB.getAllBoardItemIds('trip'),
          TravelBoardsServiceDB.getAllBoardItemIds('album'),
          TravelBoardsServiceDB.getAllBoardItemIds('collection'),
          AlbumService.getAllUserAlbums()
        ]);

        setSavedTrips(trips);

        // Handle paginated activities response
        if (activitiesResult.pagination) {
          setSavedActivities(activitiesResult.activities || []);
          setActivitiesPagination({
            total: activitiesResult.pagination.total,
            offset: activitiesResult.pagination.offset + (activitiesResult.activities?.length || 0),
            hasMore: activitiesResult.pagination.hasMore,
            loading: false
          });
        } else {
          setSavedActivities(activitiesResult || []);
          setActivitiesPagination({
            total: (activitiesResult || []).length,
            offset: (activitiesResult || []).length,
            hasMore: false,
            loading: false
          });
        }

        setAvailableTags(tags);
        setCollections(allCollections);
        setAlbums(userAlbums || []);

        if (boardsResult.success) {
          // getUserBoards includes boards the user only collaborates on. Mark
          // ownership the way the summary does, so the cards offer owner
          // actions (edit, archive, delete, share) only on their own boards.
          setTravelBoards((boardsResult.boards || []).map(board => ({
            ...board,
            is_owned: String(board.user_id) === String(currentUser?.id)
          })));
        }

        setBoardItemIds({
          activity: activityIdsResult.success ? activityIdsResult.contentIds || [] : [],
          trip: tripIdsResult.success ? tripIdsResult.contentIds || [] : [],
          album: albumIdsResult.success ? albumIdsResult.contentIds || [] : [],
          collection: collectionIdsResult.success ? collectionIdsResult.contentIds || [] : []
        });
      }
    } catch (err) {
      console.error('Error loading saved items:', err);
      setError(err.message || t('page.errorState.summaryLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load more activities (pagination)
  const loadMoreActivities = useCallback(async () => {
    if (activitiesPagination.loading || !activitiesPagination.hasMore) return;

    setActivitiesPagination(prev => ({ ...prev, loading: true }));

    try {
      const result = await SavedItemsService.getSavedActivities(null, {
        limit: ACTIVITIES_PAGE_SIZE,
        offset: activitiesPagination.offset
      });

      if (result.pagination) {
        // Dedupe by id — trip drill-ins may have merged items ahead of pagination
        setSavedActivities(prev => {
          const seen = new Set(prev.map(a => a.id));
          return [...prev, ...(result.activities || []).filter(a => !seen.has(a.id))];
        });
        // From `prev`, not this request's closure: a remove that landed while
        // the page was in flight lowered the offset (BH-21-07), and an
        // absolute write would put it back.
        setActivitiesPagination(prev => ({
          total: result.pagination.total,
          offset: prev.offset + (result.activities?.length || 0),
          hasMore: result.pagination.hasMore,
          loading: false
        }));
      }
    } catch (err) {
      console.error('Error loading more activities:', err);
      setActivitiesPagination(prev => ({ ...prev, loading: false }));
    }
  }, [activitiesPagination.loading, activitiesPagination.hasMore, activitiesPagination.offset]);

  // Fetch ALL activities saved from a specific trip (server-side provenance
  // filter) and merge them into the loaded list, so the trip drill-in shows
  // complete results even when those activities sit beyond the loaded pages.
  const loadTripActivities = useCallback(async (tripId) => {
    try {
      const result = await SavedItemsService.getSavedActivities(null, {
        sourceTripId: tripId,
        limit: 100,
        offset: 0
      });
      const fetched = result.activities || result || [];
      if (fetched.length > 0) {
        setSavedActivities(prev => {
          const seen = new Set(prev.map(a => a.id));
          return [...prev, ...fetched.filter(a => !seen.has(a.id))];
        });
      }
    } catch (err) {
      console.error('Error loading trip activities:', err);
    }
  }, []);

  // Load archived collections and boards (single consolidated endpoint)
  const loadArchivedItems = useCallback(async () => {
    try {
      const result = await SavedItemsService.getAllArchivedItems();
      if (result.success) {
        setArchivedCollections(result.collections || []);
        setArchivedBoards(result.boards || []);
      }
    } catch (err) {
      console.error('[Archive] Error loading archived items:', err);
    }
  }, []);

  // Re-read the containers — collections, boards, albums — plus board
  // membership and the archived count, leaving activities and their pagination
  // untouched. Restoring from the archive and editing board membership change
  // these server-side in ways the page can't reconstruct locally (a restored
  // collection's previews and counts, a board's item count). A full
  // loadSavedItems would flash the page skeleton and drop any activity pages
  // already loaded.
  const refreshContainers = useCallback(async () => {
    try {
      const summary = await SavedItemsService.getSummary(ACTIVITIES_PAGE_SIZE, 0);
      if (!summary?.success) return;
      setCollections(summary.collections || []);
      setAlbums(summary.albums || []);
      setTravelBoards(summary.boards || []);
      setBoardItemIds(summary.boardItemIds || { activity: [], trip: [], album: [], collection: [] });
      setArchivedTotalCount(summary.archivedCount || 0);
    } catch (err) {
      console.error('Error refreshing saved containers:', err);
    }
  }, []);

  // Invalidate the savedItems summary cache and the Home dashboard cache.
  // Called from every mutation so that re-entering this page or Home fetches
  // fresh data instead of serving stale cached payloads.
  const invalidateCachesForUser = useCallback(() => {
    invalidateSavedItemsCache(currentUserId);
    invalidateDashboardCache(currentUserId);
  }, [currentUserId]);

  // === ACTIVITY ACTIONS ===

  // Take a place whose saves are gone off the page. A loaded card's save sat
  // inside the pages already read, so the next Load More starts one row
  // earlier: left at its old offset it would skip the save that slid into the
  // gap (BH-21-07). An id that was never loaded leaves the paging alone.
  const dropSavedActivity = useCallback((activityId) => {
    if (!savedActivities.some(a => a.id === activityId)) return;
    setSavedActivities(prev => prev.filter(a => a.id !== activityId));
    setActivitiesPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - 1),
      total: Math.max(0, prev.total - 1)
    }));
  }, [savedActivities]);

  // Unsave an activity — the only way to remove a saved place. Archiving one
  // used to live here too, behind a button labelled "Unsave", with no restore.
  const unsaveActivity = useCallback(async (activityId) => {
    const result = await SavedItemsService.unsaveActivity(activityId);
    if (result.success) {
      dropSavedActivity(activityId);
      invalidateCachesForUser();
    }
    return result;
  }, [dropSavedActivity, invalidateCachesForUser]);

  // === COLLECTION ACTIONS ===

  // Archive a collection
  const archiveCollection = useCallback(async (collectionId, makePrivate = false) => {
    const result = await CollectionsService.archiveCollection(collectionId, makePrivate);
    if (result.success) {
      setCollections(prev => prev.filter(c => c.id !== collectionId));
      setArchivedTotalCount(prev => prev + 1);
      invalidateCachesForUser();
    }
    return result;
  }, [invalidateCachesForUser]);

  // Unarchive a collection. The restored collection has to reappear on the
  // Collections tab, which was loaded without it — refreshContainers re-reads it
  // with its previews and counts rather than guessing them from the archive row.
  const unarchiveCollection = useCallback(async (collectionId) => {
    const result = await CollectionsService.unarchiveCollection(collectionId);
    if (result.success) {
      setArchivedCollections(prev => prev.filter(c => c.id !== collectionId));
      setArchivedTotalCount(prev => Math.max(0, prev - 1));
      invalidateCachesForUser();
      refreshContainers();
    }
    return result;
  }, [invalidateCachesForUser, refreshContainers]);

  // Drop a deleted collection from whichever list showed it. Delete is offered
  // on archived cards as well as live ones, and an archived one also has to
  // leave the archive list and the "Archived (n)" count.
  const removeDeletedCollection = useCallback((collection) => {
    setCollections(prev => prev.filter(c => c.id !== collection.id));
    if (collection.archived_at || collection.archivedAt) {
      setArchivedCollections(prev => prev.filter(c => c.id !== collection.id));
      setArchivedTotalCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  // === BOARD ACTIONS ===

  // Archive a board
  const archiveBoard = useCallback(async (boardId, makePrivate = false) => {
    const result = await TravelBoardsServiceDB.archiveBoard(boardId, makePrivate);
    if (result.success) {
      setTravelBoards(prev => prev.filter(b => b.id !== boardId));
      setArchivedTotalCount(prev => prev + 1);
      invalidateCachesForUser();
    }
    return result;
  }, [invalidateCachesForUser]);

  // Unarchive a board — refreshContainers puts it back on the Boards tab, as for
  // collections above.
  const unarchiveBoard = useCallback(async (boardId) => {
    const result = await TravelBoardsServiceDB.unarchiveBoard(boardId);
    if (result.success) {
      setArchivedBoards(prev => prev.filter(b => b.id !== boardId));
      setArchivedTotalCount(prev => Math.max(0, prev - 1));
      invalidateCachesForUser();
      refreshContainers();
    }
    return result;
  }, [invalidateCachesForUser, refreshContainers]);

  // Drop a deleted board from whichever list showed it — see
  // removeDeletedCollection.
  const removeDeletedBoard = useCallback((board) => {
    setTravelBoards(prev => prev.filter(b => b.id !== board.id));
    if (board.archived_at || board.archivedAt) {
      setArchivedBoards(prev => prev.filter(b => b.id !== board.id));
      setArchivedTotalCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  // === ALBUM ACTIONS ===

  // Albums have no archive actions: they always belong to a trip, so they stay
  // reachable from the trip and the Planner Past tab without one.
  //
  // Remove an album from saved: take it off every board the user owns, which is
  // exactly what put it on the Albums tab. It never deletes the album — that
  // belongs on the trip's Recap tab, where the album is the author's work
  // rather than a save.
  const unsaveAlbum = useCallback(async (albumId) => {
    const result = await TravelBoardsServiceDB.removeItemFromAllMyBoards('album', albumId);
    if (result.success) {
      setAlbums(prev => prev.filter(a => String(a.id) !== String(albumId)));
      setBoardItemIds(prev => ({
        ...prev,
        album: (prev.album || []).filter(id => String(id) !== String(albumId))
      }));
      invalidateCachesForUser();
      // The boards it came off changed their item counts and previews
      refreshContainers();
    }
    return result;
  }, [invalidateCachesForUser, refreshContainers]);

  // === TRIP ACTIONS ===

  // Remove a trip from saved: off every board the user owns, as for albums
  // above — a saved trip is a board item.
  const unsaveTrip = useCallback(async (tripId) => {
    const result = await SavedItemsService.unsaveTrip(tripId);
    if (result.success) {
      setSavedTrips(prev => prev.filter(t => String(t.id) !== String(tripId)));
      setBoardItemIds(prev => ({
        ...prev,
        trip: (prev.trip || []).filter(id => String(id) !== String(tripId))
      }));
      invalidateCachesForUser();
      // The boards it came off changed their item counts and previews
      refreshContainers();
    }
    return result;
  }, [invalidateCachesForUser, refreshContainers]);

  // === WALKING TOUR ACTIONS ===

  const unsaveWalkingTour = useCallback(async (tourId) => {
    const result = await WalkingToursService.unsaveTour(tourId);
    if (result.success) {
      setSavedWalkingTours(prev => prev.filter(t => t.id !== tourId));
      invalidateCachesForUser();
    }
    return result;
  }, [invalidateCachesForUser]);

  const deleteWalkingTour = useCallback(async (tourId) => {
    const result = await WalkingToursService.deleteTour(tourId);
    if (result.success) {
      setSavedWalkingTours(prev => prev.filter(t => t.id !== tourId));
      invalidateCachesForUser();
    }
    return result;
  }, [invalidateCachesForUser]);

  // === TOURISM EXPERIENCE ACTIONS ===

  const unsaveTourismExperience = useCallback(async (experienceId) => {
    const result = await TourismExperiencesService.unsaveExperience(experienceId);
    if (result.success) {
      setSavedTourismExperiences(prev => prev.filter(e => e.id !== experienceId));
      invalidateCachesForUser();
    }
    return result;
  }, [invalidateCachesForUser]);

  // === BOARD ITEM ACTIONS ===

  // Update boardItemIds after removing an item from a board
  const updateBoardItemIds = useCallback((contentType, contentId, action = 'remove') => {
    setBoardItemIds(prev => {
      const currentIds = prev[contentType] || [];
      if (action === 'remove') {
        return {
          ...prev,
          [contentType]: currentIds.filter(id => String(id) !== String(contentId))
        };
      } else if (action === 'add') {
        return {
          ...prev,
          [contentType]: [...currentIds, contentId]
        };
      }
      return prev;
    });
  }, []);

  // === HELPER FUNCTIONS (Memoized Maps) ===

  // Memoized collection activity lookup Map for O(1) lookups
  const collectionActivityMap = useMemo(() => {
    const map = new Map();
    collections.forEach(collection => {
      // Under any of the place's ids: the server sends one card per place,
      // which may carry the other id (a save kept under 'atr_' in one list).
      const activityIds = new Set(collection.equivalentActivityIds || collection.activityIds || []);
      map.set(collection.id, activityIds);
    });
    return map;
  }, [collections]);

  // Memoized activity lookup by ID for O(1) lookups
  const activityById = useMemo(() => {
    const map = new Map();
    savedActivities.forEach(activity => map.set(activity.id, activity));
    return map;
  }, [savedActivities]);

  // Get activities in a specific collection (uses memoized Map for O(1) lookups)
  // Falls back to previewActivities from the summary endpoint when paginated
  // activities don't contain the needed items
  const getActivitiesInCollection = useCallback((collectionId) => {
    if (!collectionId) return savedActivities;
    const activityIds = collectionActivityMap.get(collectionId);
    if (!activityIds || activityIds.size === 0) return [];
    // Use activityById map for O(1) lookups instead of filter
    const activities = [];
    activityIds.forEach(id => {
      const activity = activityById.get(id);
      if (activity) activities.push(activity);
    });
    // If no loaded activities matched (due to pagination), use server-provided previews
    if (activities.length === 0) {
      const collection = collections.find(c => c.id === collectionId);
      if (collection?.previewActivities?.length > 0) {
        return collection.previewActivities.map(p => ({
          id: p.id || p.contentId,
          contentId: p.contentId,
          imageUrl: p.imageUrl,
          data: {
            title: p.title,
            imageUrl: p.imageUrl,
            category: p.category
          }
        }));
      }
    }
    return activities;
  }, [savedActivities, collectionActivityMap, activityById, collections]);

  // === COMPUTED VALUES ===

  // Create unified feed of organized content + unorganized items
  const unifiedFeed = useMemo(() => {
    const feedItems = [];

    // Build set of activity IDs that are in any collection
    const activitiesInCollections = new Set();
    collections.forEach(collection => {
      const activityIds = collection.equivalentActivityIds || collection.activityIds || [];
      activityIds.forEach(id => activitiesInCollections.add(id));
    });

    // Sets of IDs organized in boards
    const activitiesInBoards = new Set((boardItemIds.activity || []).map(id => String(id)));
    const tripsInBoards = new Set((boardItemIds.trip || []).map(id => String(id)));
    const albumsInBoards = new Set((boardItemIds.album || []).map(id => String(id)));
    const collectionsInBoards = new Set((boardItemIds.collection || []).map(id => String(id)));

    // Add collections not in boards
    collections.forEach(collection => {
      if (!collectionsInBoards.has(String(collection.id))) {
        feedItems.push({
          type: 'collection',
          id: collection.id,
          data: collection,
          sortDate: new Date(collection.updatedAt || collection.createdAt || 0).getTime()
        });
      }
    });

    // Add albums not in boards
    albums.forEach(album => {
      if (!albumsInBoards.has(String(album.id))) {
        feedItems.push({
          type: 'album',
          id: album.id,
          data: album,
          sortDate: new Date(album.updated_at || album.created_at || 0).getTime()
        });
      }
    });

    // Add trips not in boards
    savedTrips.forEach(trip => {
      if (!tripsInBoards.has(String(trip.id))) {
        feedItems.push({
          type: 'trip',
          id: trip.id,
          data: trip,
          sortDate: new Date(trip.savedAt || 0).getTime()
        });
      }
    });

    // Add uncategorized activities
    savedActivities.forEach(activity => {
      if (!activitiesInCollections.has(activity.id) && !activitiesInBoards.has(String(activity.id))) {
        feedItems.push({
          type: 'activity',
          id: activity.id,
          data: activity,
          sortDate: new Date(activity.savedAt || 0).getTime()
        });
      }
    });

    // Add saved walking tours
    savedWalkingTours.forEach(tour => {
      feedItems.push({
        type: 'walking_tour',
        id: tour.id,
        data: tour,
        sortDate: new Date(tour.saved_at || tour.created_at || 0).getTime()
      });
    });

    // Add saved tourism experiences
    savedTourismExperiences.forEach(exp => {
      feedItems.push({
        type: 'tourism_experience',
        id: exp.id,
        data: exp,
        sortDate: new Date(exp.saved_at || exp.created_at || 0).getTime()
      });
    });

    return feedItems.sort((a, b) => b.sortDate - a.sortDate);
  }, [collections, albums, savedTrips, savedActivities, savedWalkingTours, savedTourismExperiences, boardItemIds]);

  // Filter the unified feed based on search
  const filteredFeed = useMemo(() => {
    if (!filters.search) return unifiedFeed;

    const searchLower = filters.search.toLowerCase();
    return unifiedFeed.filter(item => {
      switch (item.type) {
        case 'collection':
          return item.data.name?.toLowerCase().includes(searchLower) ||
                 item.data.description?.toLowerCase().includes(searchLower);
        case 'album':
          return item.data.album_name?.toLowerCase().includes(searchLower) ||
                 item.data.album_description?.toLowerCase().includes(searchLower) ||
                 item.data.trip_name?.toLowerCase().includes(searchLower);
        case 'trip':
          return item.data.data?.name?.toLowerCase().includes(searchLower) ||
                 item.data.data?.location?.toLowerCase().includes(searchLower);
        case 'activity':
          return item.data.data?.title?.toLowerCase().includes(searchLower) ||
                 item.data.data?.description?.toLowerCase().includes(searchLower);
        case 'walking_tour':
        case 'tourism_experience':
          return item.data.title?.toLowerCase().includes(searchLower) ||
                 item.data.description?.toLowerCase().includes(searchLower) ||
                 item.data.location?.toLowerCase().includes(searchLower);
        default:
          return true;
      }
    });
  }, [unifiedFeed, filters.search]);

  return {
    // Data
    currentUserId,
    savedTrips,
    savedActivities,
    collections,
    albums,
    travelBoards,
    savedWalkingTours,
    savedTourismExperiences,
    availableTags,
    boardItemIds,
    loading,
    error,

    // Archived data (collections and boards — the only archivable types)
    archivedCollections,
    archivedBoards,
    archivedTotalCount,

    // Pagination
    activitiesPagination,

    // Filters
    filters,
    setFilters,
    availableFilters,

    // Computed data
    filteredFeed,

    // Helper functions
    getActivitiesInCollection,
    collectionActivityMap,
    activityById,

    // Data loading actions
    loadSavedItems,
    loadMoreActivities,
    loadTripActivities,
    loadArchivedItems,

    // Activity actions
    unsaveActivity,
    dropSavedActivity,

    // Collection actions
    archiveCollection,
    unarchiveCollection,
    removeDeletedCollection,

    // Board actions
    archiveBoard,
    unarchiveBoard,
    removeDeletedBoard,

    // Album actions
    unsaveAlbum,

    // Container refresh (collections, boards, albums, archived count)
    refreshContainers,

    // Trip actions
    unsaveTrip,

    // Walking tour actions
    unsaveWalkingTour,
    deleteWalkingTour,

    // Tourism experience actions
    unsaveTourismExperience,

    // Board item actions
    updateBoardItemIds,

    // Setters for external updates
    setSavedActivities,
    setArchivedCollections,
    setArchivedBoards,
    setCollections,
    setAlbums,
    setTravelBoards,
    setBoardItemIds
  };
}

export default useSavedItemsData;
